"use client";

import { useState, useEffect, useMemo } from "react";
import { useQuery } from '@tanstack/react-query';
import { apiGetAllNews } from '@/services/newsHome/api';

// Danh sách ảnh fallback khi không có dữ liệu từ API
const FALLBACK_IMAGES = [
    '/images/About/Rectangle 4528.png',
    '/images/About/Rectangle 4529.png',
    '/images/About/Rectangle 4530.png',
];

const ABOUT_ID = Number(process.env.NEXT_PUBLIC_ABOUT_US_NEWS_TYPE_ID);

const ImageCarouselGallery = () => {

    // Gọi API lấy danh sách bài viết theo loại
    const { data: newsData } = useQuery({
        queryKey: ['gallery-images-content-2101'],
        queryFn: async () => {
            const res = await apiGetAllNews({
                keywords: '',
                newsTypeId: ABOUT_ID,
                isPublic: '',
                page: 1,
                pageSize: 50,
            });
            return res?.isOK ? res.result.items : [];
        },
        staleTime: 5 * 60 * 1000,
    });

    // Xử lý trích xuất danh sách ảnh từ nội dung bài viết
    const images = useMemo(() => {
        // Kiểm tra dữ liệu danh sách bài viết
        if (newsData && Array.isArray(newsData)) {

            // Tìm bài viết dùng cho thư viện ảnh
            const galleryPost = newsData.find((item: any) =>
                item.seoDescription === 'Hình ảnh'
            );

            // Lấy danh sách ảnh từ nội dung HTML
            if (galleryPost && galleryPost.content) {
                const regex = /<img[^>]+src="([^">]+)"/g;
                const matches = [];
                let match;

                // Trích xuất toàn bộ src của thẻ img
                while ((match = regex.exec(galleryPost.content)) !== null) {
                    matches.push(match[1]);
                }

                // Trả về danh sách ảnh nếu tồn tại
                if (matches.length > 0) {
                    return matches;
                }
            }
        }

        // Trả về ảnh fallback khi không có dữ liệu hợp lệ
        return FALLBACK_IMAGES;
    }, [newsData]);


    // State quản lý ảnh hiện tại
    const [currentIndex, setCurrentIndex] = useState(1);
    // State quản lý hướng chuyển slide
    const [direction, setDirection] = useState<'next' | 'prev'>('next');

    // Tự động chuyển ảnh theo thời gian
    useEffect(() => {
        if (images.length <= 1) return;

        const interval = setInterval(() => {
            setDirection('next');
            setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
        }, 3000);

        return () => clearInterval(interval);
    }, [images.length]);

    // Xử lý khi click chọn ảnh
    const handleImageClick = (index: number) => {
        if (index !== currentIndex) {
            setDirection(index > currentIndex ? 'next' : 'prev');
            setCurrentIndex(index);
        }
    };

    // Tính toán danh sách ảnh hiển thị trong carousel
    const getVisibleImages = () => {
        const visible = [];
        const len = images.length;
        if (len === 0) return [];

        for (let i = -1; i <= 1; i++) {
            const index = (currentIndex + i + len) % len;
            visible.push({
                src: images[index],
                index: index,
                position: i,
            });
        }
        return visible;
    };

    return (
        <section className="-mt-8 pt-5 pb-6 2xl:pb-0 bg-white overflow-hidden">
            {/* Định nghĩa animation cho hiệu ứng chuyển slide */}
            <style jsx>{`
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(50px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes slideInLeft {
                    from { opacity: 0; transform: translateX(-50px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                .animate-slide-next {
                    animation: slideInRight 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
                }
                .animate-slide-prev {
                    animation: slideInLeft 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
                }
            `}</style>

            {/* Khung container của carousel */}
            <div className="max-w-[1400px] 2xl:max-w-[1700px] min-[1920px]:max-w-full min-[1920px]:px-20 mx-auto px-4">

                <div
                    key={currentIndex}
                    className={`
                        relative flex justify-center items-center gap-2 xl:gap-4 2xl:gap-8 min-[1920px]:gap-12 h-[240px] xl:h-[420px] 2xl:h-[580px] min-[1920px]:h-[750px]
                        ${direction === 'next' ? 'animate-slide-next' : 'animate-slide-prev'}
                    `}
                >
                    {getVisibleImages().map((img, idx) => {
                        const isCenter = img.position === 0;

                        return (
                            <div
                                key={`${img.index}-${idx}`}
                                onClick={() => handleImageClick(img.index)}
                                className={`
                                    relative cursor-pointer overflow-hidden
                                    rounded-lg md:rounded-xl
                                    transition-all duration-500 ease-in-out
                                    ${isCenter
                                        ? "flex-[0_0_75%] md:flex-[0_0_63%] h-full z-20 brightness-100 opacity-100"
                                        : "flex-[0_0_12%] md:flex-[0_0_60%] h-full z-10 brightness-50 opacity-90"
                                    }
                                `}
                            >
                                <img
                                    src={img.src}
                                    alt={`Gallery ${img.index + 1}`}
                                    className={`
                                        w-full h-full object-cover
                                        transition-transform duration-700 ease-in-out
                                        ${isCenter ? "scale-100" : "scale-103"}
                                    `}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default ImageCarouselGallery;
